/**
 * Money formatting for the analytics surface, in the company's REPORTING CURRENCY
 * (VERA-260).
 *
 * ## Why this module exists
 *
 * There were three hand-rolled currency formatters on this surface, and all three
 * hardcoded `'USD'`:
 *
 *   `metricInputs/metricInputFormat.ts`  the `currencyFormatter` behind `formatCents`
 *   `RoiValueChart.tsx`                  `fmtCurrency` + a `$`-templated axis
 *   `RoiHero.tsx`                        a byte-identical copy of the chart's
 *
 * Working-conventions §36 says one hand-roll is allowed and the second is a shared
 * component; this was the third, and it had already drifted (the chart's axis is not
 * `Intl` at all — it interpolates a literal `$`, so a symbol swap there is a real code
 * change rather than a parameter). Hardcoding was defensible while nothing in the
 * system knew what currency a brand reports in. `companies.reporting_currency` now
 * does, so it is not.
 *
 * ## The currency is a FACT ABOUT THE FIGURE, not a display preference
 *
 * `IRoiMetrics.reporting_currency` decides which rows are IN the money aggregates —
 * transfers priced in another currency are excluded and counted, never converted (see
 * the migration 0096 header for why read-time FX is rejected). So rendering a figure
 * with the wrong symbol is not a cosmetic slip: it mislabels an amount that was
 * computed under a different definition.
 *
 * That is also why every function here TAKES the currency rather than reading a module
 * constant. A default that silently applies when a caller forgets is exactly how the
 * `'USD'` literals spread in the first place.
 */

import { DEFAULT_REPORTING_CURRENCY } from '@repo/types'

/**
 * `Intl.NumberFormat` is not free to construct and these run inside chart tooltips and
 * per-card renders, so instances are memoised per (currency, precision). Keyed rather
 * than held in a single mutable slot because a page can legitimately render two
 * currencies at once — a brand's own figures beside a Vera-spend total settled in
 * Stripe's currency, which is a DIFFERENT currency and known not to be reconciled.
 */
const cache = new Map<string, Intl.NumberFormat>()

/**
 * `fractionDigits: null` means "whatever this currency's own convention is", and that
 * is not a stylistic nicety — JPY and KRW are ZERO-DECIMAL currencies, so forcing two
 * places renders `¥400.00`, which is not a form yen is ever written in. `Intl` already
 * knows each currency's exponent; the previous formatters hardcoded `2` and could
 * afford to because they also hardcoded USD.
 */
const formatter = (
  currency: string,
  fractionDigits: number | null,
): Intl.NumberFormat => {
  const key = `${currency}:${fractionDigits ?? 'auto'}`
  const hit = cache.get(key)
  if (hit) return hit

  const digits =
    fractionDigits === null
      ? {}
      : {
          minimumFractionDigits: fractionDigits,
          maximumFractionDigits: fractionDigits,
        }

  /*
    A currency code the runtime does not recognise makes `Intl.NumberFormat` THROW,
    and it would throw during render — a blank analytics page rather than a wrong
    symbol. The API validates against a curated allowlist and the DB CHECKs the shape,
    so this should be unreachable; it is caught anyway because the failure mode is so
    much worse than the fault. Falling back to the default currency keeps the digits
    (which are correct) and loses only the symbol.
  */
  let made: Intl.NumberFormat
  try {
    made = new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      ...digits,
    })
  } catch {
    made = new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: DEFAULT_REPORTING_CURRENCY,
      ...digits,
    })
  }

  cache.set(key, made)
  return made
}

/** Cents → `"$1,234.00"` / `"£1,234.00"`. The exact form, for a card figure. */
export const formatMoneyCents = (
  cents: number,
  currency: string = DEFAULT_REPORTING_CURRENCY,
): string => formatter(currency, null).format(cents / 100)

/**
 * Cents → `"$1,234"`. Whole units, for the hero and the chart tooltip.
 *
 * Those two surfaces drop the decimals deliberately: the hero reports a magnitude a
 * reader compares against spend, and two decimal places on a five-figure total is
 * precision that carries no information while costing width.
 */
export const formatMoneyCentsCompact = (
  cents: number,
  currency: string = DEFAULT_REPORTING_CURRENCY,
): string => formatter(currency, 0).format(cents / 100)

/**
 * The currency's own symbol — `"$"`, `"£"`, `"¥"` — with no amount attached.
 *
 * Derived by formatting zero and stripping the digits, rather than kept as a hand-
 * written code→symbol map. A second map would be a second thing to update when the
 * currency catalogue grows, and it would disagree with the formatter above the first
 * time somebody added a code to one and not the other. Where a runtime has no symbol
 * it returns the code itself, which is what `Intl` already does and is the correct
 * answer rather than a fallback.
 */
export const moneySymbol = (
  currency: string = DEFAULT_REPORTING_CURRENCY,
): string => formatter(currency, 0).format(0).replace(/[\d\s.,  ]/g, '')

/**
 * Compact money for a chart Y axis — `"$1.2k"`, `"£340"`.
 *
 * Hand-composed rather than `Intl`'s `notation: 'compact'`, because that renders
 * `"$1.2K"` with a capital K and rounds to one significant digit in a way that makes
 * neighbouring ticks collide (`$1K`, `$1K`, `$2K`). The thousands step here is chosen
 * to keep adjacent ticks distinguishable, which is the axis's whole job.
 *
 * The symbol comes from `moneySymbol` rather than a literal `$` — the bug this
 * replaces, where the axis of a GBP brand's chart read in dollars while the tooltip
 * above it read in pounds.
 */
export const formatMoneyAxis = (
  cents: number,
  currency: string = DEFAULT_REPORTING_CURRENCY,
): string => {
  const symbol = moneySymbol(currency)
  const units = cents / 100
  if (Math.abs(units) >= 1000) {
    return `${symbol}${(units / 1000).toFixed(units % 1000 === 0 ? 0 : 1)}k`
  }
  return `${symbol}${Math.round(units)}`
}
